import { ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import SwitchComponent from '../../components/switch';
import { AdornedInput, Input, Muted, Strong } from '../../components/ui';

/** Small form controls shared by the Provider pages (spec 019). */

const ToggleRowRoot = styled.div`
  ${tw`flex items-start justify-between gap-4 py-2`}
`;

export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
  testId,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <ToggleRowRoot>
      <div>
        <Strong>{label}</Strong>
        {hint && (
          <div>
            <Muted $size="sm">{hint}</Muted>
          </div>
        )}
      </div>
      <SwitchComponent
        isActive={checked}
        onChange={() => {
          if (!disabled) onChange(!checked);
        }}
        inputTestId={testId}
        labelTestId={testId ? `${testId}-label` : undefined}
      />
    </ToggleRowRoot>
  );
}

export function MoneyInput({
  id,
  value,
  onChange,
  placeholder,
  invalid,
  disabled,
  trailing,
  testId,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  trailing?: ReactNode;
  testId?: string;
}) {
  return (
    <AdornedInput
      id={id}
      leading="$"
      trailing={trailing}
      inputMode="decimal"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      onChange={event => onChange(event.target.value)}
      data-testid={testId}
    />
  );
}

export function NumberInput({
  id,
  value,
  onChange,
  trailing,
  invalid,
  testId,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  trailing?: ReactNode;
  invalid?: boolean;
  testId?: string;
}) {
  if (trailing) {
    return (
      <AdornedInput
        id={id}
        inputMode="numeric"
        trailing={trailing}
        value={value}
        aria-invalid={invalid || undefined}
        onChange={event => onChange(event.target.value)}
        data-testid={testId}
      />
    );
  }
  return (
    <Input
      id={id}
      inputMode="numeric"
      value={value}
      aria-invalid={invalid || undefined}
      onChange={event => onChange(event.target.value)}
      data-testid={testId}
    />
  );
}

const CheckLabel = styled.label`
  ${tw`flex items-center gap-3 py-1 text-sm cursor-pointer`}
  color: var(--color-text-primary);
  input {
    width: 18px;
    height: 18px;
    accent-color: var(--color-black);
  }
`;

export function CheckRow({
  label,
  checked,
  onChange,
  testId,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId?: string;
}) {
  return (
    <CheckLabel>
      <input
        type="checkbox"
        checked={checked}
        onChange={event => onChange(event.target.checked)}
        data-testid={testId}
      />
      <span>{label}</span>
    </CheckLabel>
  );
}

/** A warning line under a price (D26): shown, never blocking. */
export const WageLine = styled.p<{ $below?: boolean }>`
  ${tw`mt-1 text-xs`}
  color: ${({ $below }) => ($below ? '#B45309' : 'var(--color-text-secondary)')};
  font-weight: ${({ $below }) => ($below ? 600 : 400)};
`;
